/*
 * Purpose: Client site analytics — consent-gated event flush to API
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { getAnonymousId, hasAnalyticsConsent } from '@/lib/analyticsConsent';
import { apiUrl } from '@/lib/apiBase';
import { bufferLocalAnalyticsEvent } from '@/lib/localAnalyticsBuffer';
import type { AnalyticsEventPayload, AnalyticsEventType } from '@/lib/siteAnalyticsTypes';

const queue: AnalyticsEventPayload[] = [];
let flushing = false;
let analyticsEndpointDisabled = false;

export function trackAnalytics(
  type: AnalyticsEventType,
  options: Omit<AnalyticsEventPayload, 'type'> = {}
): void {
  if (!hasAnalyticsConsent()) return;

  queue.push({
    type,
    path: options.path ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
    meta: options.meta,
    anonymousId: getAnonymousId(),
    tenantId: options.tenantId ?? null,
  });

  bufferLocalAnalyticsEvent(queue[queue.length - 1]);

  if (queue.length >= 8) {
    void flushAnalytics();
  }
}

export async function flushAnalytics(): Promise<void> {
  if (!hasAnalyticsConsent() || flushing || queue.length === 0 || analyticsEndpointDisabled) return;
  flushing = true;
  const batch = queue.splice(0, queue.length);

  try {
    await Promise.all(batch.map(async (event) => {
      const response = await fetch(apiUrl('/api/analytics/event'), {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Analytics-Consent': '1',
          },
          body: JSON.stringify(event),
      });
      if (!response.ok) {
        analyticsEndpointDisabled = true;
      }
    }));
  } catch {
    queue.unshift(...batch);
    analyticsEndpointDisabled = true;
  } finally {
    flushing = false;
  }
}

export function trackPageView(path?: string): void {
  trackAnalytics('page_view', { path });
}

export function trackSessionStart(): void {
  trackAnalytics('session_start');
}

export function trackError(message: string, meta?: Record<string, unknown>): void {
  trackAnalytics('error', { meta: { message, ...meta } });
}

export function trackExperimentEvent(
  experimentId: string,
  variant: string,
  action: 'impression' | 'pricing_table_view' | 'support_click',
  meta?: Record<string, unknown>
): void {
  trackAnalytics('custom', {
    path: typeof window !== 'undefined' ? window.location.pathname : '/pricing',
    meta: {
      experiment: experimentId,
      variant,
      action,
      ...meta,
    },
  });
}
